/**
 * @namespace services
 * @category services
 * @subcategory diagnostics
 * @module diagnostics-service
 **/

import anime from 'animejs/lib/anime.es.js'
import { createLocalStorage } from '@/services/cache-service'
import store from '@/store'

/**
 * Used by Diagnostics module to determine whenever to show/hide tor/ana by default
 */
export const defaultSensorsVisibility = {
  ana: true,
  tor: true,
}

/**
 * Used for store some diagnostic settings
 */
const storage = createLocalStorage('diagnostic')

/**
 * This function creates a replay controller object that manages the transition of the current position index in a replay animation. It provides functions to play, pause, move to the previous or next position, jump to a specific index, and destroy the animation. The controller includes options for controlling the animation, such as speed, minimum and maximum values, and callback functions for animation events.
 * @inner
 * @returns {Object} The replay controller object with functions to control the replay animation.
 */
export function createReplayController() {
  /**
   * @name DiagnosticsReplayControllerInstance
   * @instance
   */
  let scope = {
    /**
     * Object representing the current animation.
     * @property {Object} currentAnimationObject
     */
    currentAnimationObject: null,
    /**
     * Current animation.
     * @type {Object}
     */
    currentAnimation: null,
    /**
     * Flag indicating if the current animation is playing.
     * @type {boolean}
     */
    currentAnimationOptions: {},
    /**
     * Flag indicating if the current animation is playing.
     * @type {boolean}
     */
    currentAnimationPlaying: false,
    /**
     * Checks if the animation was created.
     * @function wasCreated
     * @memberof DiagnosticsReplayControllerInstance
     * @returns {boolean} - Indicates if the animation was created.
     */
    wasCreated() {
      return !!scope.currentAnimation
    },
    /**
     * @warn unused
     * Proof of concept for smoothly transitioning between two latitude-longitude positions using the anime library.
     * @param {*} latLng1
     * @param {*} latLng2
     * @param {*} options
     */
    transitionLatLng(latLng1, latLng2, options) {
      let target = {
        lat: latLng1[0],
        lng: latLng1[1],
      }
      anime({
        ...options,
        targets: target,
        lat: latLng2[0],
        lng: latLng2[1],
        update() {
          options.onUpdate(target.lat, target.lng)
        },
      })
    },
    /**
     * Destroys the current animation.
     */
    destroyAnimation() {
      if (!this.wasCreated()) {
        return
      }

      console.debugVerbose(8, 'Replay animation destroyed')

      // Pause the current animation if it is playing
      if (this.currentAnimationPlaying) {
        scope.currentAnimation.pause()
      }

      // Remove the current animation object
      scope.currentAnimation.remove(this.currentAnimationObject)
      scope.currentAnimation = null

      // Call the onDestroy callback function from options if available
      scope.currentAnimationOptions.onDestroy &&
        scope.currentAnimationOptions.onDestroy()

      // Set the current animation playing status to false
      scope.currentAnimationPlaying = false

      // Call the onToggle callback function from options to update the play/stop status
      scope.currentAnimationOptions.onToggle &&
        scope.currentAnimationOptions.onToggle(scope.currentAnimationPlaying)

      // Log a message indicating the destruction of the animation
      console.debugVerbose(8, 'replay::destroyAnimation')
    },
    /**
     * Jumps to a specific index in the animation.
     * @param {number} index - The index to jump to.
     */
    jumpToIndex(index) {
      console.debugVerbose(8, 'replay::jumpToIndex', index)
      this.destroyAnimation()
      scope.currentAnimationObject = {
        start: index,
        index,
      }
    },
    /**
     * Toggles play or stop of the animation.
     * @param {Object} options - Options for controlling the animation.
     * @param {Number} options.max - The maximum value for the animation.
     * @param {Number} options.min - The minimum value for the animation.
     * @param {Function} options.onAnimate - Callback function for animation updates.
     * @param {Number} options.currentIndex - The current index of the animation (set/get).
     * @param {Function} options.onToggle - Callback function for toggling play or stop of the animation.
     */
    playToggle(options = {}) {
      if (options.value === false) {
        if (scope.currentAnimationPlaying) {
          scope.currentAnimation.pause()
          scope.currentAnimationPlaying = false
          //console.debugVerbose(8,'replay::stop')
          options.onToggle && options.onToggle(scope.currentAnimationPlaying)
        }

        return
      }

      if (typeof options.currentIndex !== 'number') {
        throw new Error('options.currentIndex is not a number')
      }

      if (!scope.wasCreated()) {
        //Create

        if (!scope.currentAnimationObject) {
          scope.currentAnimationObject = {
            start: options.currentIndex,
            index: options.currentIndex,
          }
        }

        scope.currentAnimationPlaying = true
        options.onToggle && options.onToggle(scope.currentAnimationPlaying)
        window.replayAnimOptions = scope.currentAnimationOptions = options
        scope.currentAnimation = window.replayAnim = anime({
          targets: scope.currentAnimationObject,
          //index: options.max,
          //easing: 'linear',
          //duration: 10000,
          ...options.animationOptions,
          round: 1,
          update: function () {
            options.currentIndex = scope.currentAnimationObject.index
            options.onAnimate(scope.currentAnimationObject.index)
          },
          complete() {
            scope.jumpToIndex(0)
          },
        })
        //console.debugVerbose(8,'replay::create-and-play')
      } else {
        //Play or pause
        if (scope.currentAnimationPlaying) {
          scope.currentAnimation.pause()
          scope.currentAnimationPlaying = false
          //console.debugVerbose(8,'replay::stop')
        } else {
          scope.currentAnimation.play()
          scope.currentAnimationPlaying = true
          //console.debugVerbose(8,'replay::play')
        }
        options.onToggle && options.onToggle(scope.currentAnimationPlaying)
      }
    },
    /**
     * Moves the animation to the previous position.
     * @param {Object} options - Options for controlling the animation.
     * @param {Number} options.max - The maximum value for the animation.
     * @param {Number} options.min - The minimum value for the animation.
     * @param {Function} options.onAnimate - Callback function for animation updates.
     * @param {Number} options.currentIndex - The current index of the animation (set/get).
     */
    prev(options = {}) {
      scope.currentAnimationOptions = options
      let index =
        options.min == options.currentIndex - 1
          ? options.max
          : options.currentIndex - 1
      options.currentIndex = index
      this.jumpToIndex(index)
      options.onAnimate(index)
    },
    /**
     * Moves the animation to the next position.
     * @param {Object} options - Options for controlling the animation.
     * @param {Number} options.max - The maximum value for the animation.
     * @param {Number} options.min - The minimum value for the animation.
     * @param {Function} options.onAnimate - Callback function for animation updates.
     * @param {Number} options.currentIndex - The current index of the animation (set/get).
     */
    next(options = {}) {
      scope.currentAnimationOptions = options
      let index =
        options.max == options.currentIndex + 1 ? 0 : options.currentIndex + 1
      options.currentIndex = index
      this.jumpToIndex(index)
      options.onAnimate(index)
    },
  }

  return scope
}

export const diagnosticsChartReferenceData = {
  normalGPS: {
    name: 'normalGPS',
    label: 'diagnostics.chart.category.normal_gps_positions',
    color: 'var(--color-gps)',
    category: 'mandatory',
    isToggleDisabled: true,
    type: 'plot',
    order: 0,
  },
  speed: {
    name: 'speed',
    label: 'diagnostics.chart.category.speed',
    color: 'var(--color-speed)',
    category: 'mandatory',
    isToggleDisabled: true,
    type: 'line',
    order: 2,
  },
  canRPM: {
    name: 'canRPM',
    label: 'diagnostics.chart.category.canRPM',
    color: 'var(--color-can-rpm)',
    category: 'can',
  },
  sensorCanFuelPerc: {
    name: 'sensorCanFuelPerc',
    label: 'diagnostics.chart.category.fuelLevelPerc',
    color: 'var(--color-can-fuel-level-perc)',
    category: 'can',
  },
  sensorCanConsumptionLiters: {
    name: 'sensorCanConsumptionLiters',
    label: 'diagnostics.chart.category.sensorCanConsumptionLiters',
    color: 'var(--color-can-sensorCanConsumptionLiters)',
    category: 'can',
  },
  sensorCanFuelLiters: {
    name: 'sensorCanFuelLiters',
    label: 'diagnostics.chart.category.fuelLevelLiters',
    color: 'var(--color-can-fuel-level-liters)',
    category: 'can',
  },
  sensorCanFuelLevel: {
    name: 'sensorCanFuelLevel',
    label: 'diagnostics.chart.category.fuelLevelPerc',
    color: 'var(--color-can-fuel-level-liters)',
    category: 'can',
  },
  vehicleDistance: {
    name: 'vehicleDistance',
    label: 'diagnostics.chart.category.vehicleDistance',
    color: 'var(--color-can-vehicle-distance)',
    category: 'can',
  },
  sensorCanBrakePedal: {
    name: 'sensorCanBrakePedal',
    label: 'diagnostics.chart.category.sensorCanBrakePedal',
    color: 'var(--color-can-brake-pedal)',
    category: 'can',
  },
  sensorCanBatteryPerc: {
    name: 'sensorCanBatteryPerc',
    label: 'diagnostics.chart.category.sensorCanBatteryPerc',
    color: 'var(--color-can-sensorCanBatteryPerc)',
    category: 'can',
  },
  sensorCanThrottle: {
    name: 'sensorCanThrottle',
    label: 'diagnostics.chart.category.sensorCanThrottle',
    color: 'var(--color-acceleration)',
    category: 'can',
  },
  speHarshBraking: {
    name: 'speHarshBraking',
    label: 'diagnostics.chart.category.speHarshBraking',
    color: 'var(--color-harsh-braking)',
    category: 'can_spe',
  },
  speHarshAcceleration: {
    name: 'speHarshAcceleration',
    label: 'diagnostics.chart.category.speHarshAcceleration',
    color: 'var(--color-harsh-acceleration)',
    category: 'can_spe',
  },
  speHarshCornering: {
    name: 'speHarshCornering',
    label: 'diagnostics.chart.category.speHarshCornering',
    color: 'var(--color-harsh-speHarshCornering)',
    category: 'can_spe',
  },
  speExcessiveSpeed: {
    name: 'speExcessiveSpeed',
    label: 'diagnostics.chart.category.speExcessiveSpeed',
    color: 'var(--color-harsh-speExcessiveSpeed)',
    category: 'can_spe',
  },
  torItems: generateChartReferenceDataForDynamicSensor('tor'),
  anaItems: generateChartReferenceDataForDynamicSensor('ana'),
  sweepWaterLeft: {
    name: 'sweepWaterLeft',
    color: 'var(--color-sensor-sweep-pump)', // Using the same color as Pompe d'humectage
  },
  sweepWaterRight: {
    name: 'sweepWaterRight',
    color: 'var(--color-sensor-sweep-pump)', // Using the same color as Pompe d'humectage
  },
  sweepFanSp: {
    name: 'sweepFanSp',
    color: 'var(--color-sensor-sweep-fan-sp)',
  },
  sweepFan: {
    name: 'sweepFan',
    color: 'var(--color-sensor-sweep-fan)',
  },
  sweepBrushLeft: {
    name: 'sweepBrushLeft',
    color: 'var(--color-sensor-sweep-brush-sp)', // Using the same color as Vitesse de la brosse
  },
  sweepBrushRight: {
    name: 'sweepBrushRight',
    color: 'var(--color-sensor-sweep-brush-sp)', // Using the same color as Vitesse de la brosse
  },
  sweepBrushCentral: {
    name: 'sweepBrushCentral',
    color: 'var(--color-sensor-sweep-brush-sp)', // Using the same color as Vitesse de la brosse
  },
  sweepBrushSp: {
    name: 'sweepBrushSp',
    color: 'var(--color-sensor-sweep-brush-sp)',
  },
  sweepLevel: {
    name: 'sweepLevel',
    color: 'var(--color-sensor-sweep-level)',
  },
  sweepRearDoor: {
    name: 'sweepRearDoor',
    color: 'var(--color-sensor-sweep-rear-door)',
  },
  sweepPump: {
    name: 'sweepPump',
    color: 'var(--color-sensor-sweep-pump)',
  },
  sweepHopRaised: {
    name: 'sweepHopRaised',
    color: 'var(--color-sensor-sweep-level)', // Using the same color as Niveau d'eau dans la cuve
  },
  sweepHpPump: {
    name: 'sweepHpPump',
    color: 'var(--color-sensor-sweep-pump)', // Using the same color as Pompe d'humectage
  },
  sweepWaterCentral: {
    name: 'sweepWaterCentral',
    color: 'var(--color-sensor-sweep-pump)', // Using the same color as Pompe d'humectage
  },
  sweepWaterFront: {
    name: 'sweepWaterFront',
    color: 'var(--color-sensor-sweep-pump)', // Using the same color as Pompe d'humectage
  },
  sweepWaterLaterals: {
    name: 'sweepWaterLaterals',
    color: 'var(--color-sensor-sweep-pump)', // Using the same color as Pompe d'humectage
  },
  washPressure: {
    name: 'washPressure',
    color: 'var(--color-sensor-sweep-pump)', // Assigning color from cleaning sensor colors
  },
  washLevel: {
    name: 'washLevel',
    color: 'var(--color-sensor-sweep-level)', // Assigning color from cleaning sensor colors
  },
}

/**
 * @internal
 * Will compute tor/ana reference items assigning predefined colors from style variables
 * @see colors.scss
 * @param {String} sensorName tor/ana
 */
function generateChartReferenceDataForDynamicSensor(sensorName = 'tor') {
  let arr = []
  for (let i = 1; i < 9; i++) {
    arr.push({
      name: `${sensorName.toLowerCase()}${i}`,
      label: `${sensorName.toUpperCase()} ${i}`,
      color: `var(--color-sensor-${sensorName.toLowerCase()}${i})`,
      category: sensorName.toLowerCase(),
    })
  }
  return arr
}

/**
 * Will inject startTimestamp/endTimestamp to TORs with measureType "temps" (Those attributes are then used to render a duration chart)
 * @param {*} position
 * @param {*} activeTorPositions
 * @param {*} sensorAssignments
 */
export function normalizePositionTorsForDiagnosticsModule(
  position,
  activeTorPositions,
  sensorAssignments
) {
  if (!position.sensorTor) {
    throw new Error('No sensorTor property found')
  }
  if (!activeTorPositions) {
    throw new Error('activeTorPositions required')
  }
  if (!sensorAssignments) {
    throw new Error('sensorAssignments required')
  }

  // Determine if this is the first or last position in the dataset
  const isFirstPosition = position.isFirstPosition === true
  const isLastPosition = position.isLastPosition === true

  /* console.log(`diagnostics-service normalizePositionTorsForDiagnosticsModule`, {
    timestamp: position.timestamp,
    isFirstPosition,
    isLastPosition,
    torCount: position.sensorTor.length
  }); */

  position.sensorTor.forEach((sensorItem) => {
    //Use name from box configuration assignment and fallback to name from positions response
    let relatedSensorAssignment = sensorAssignments.find(
      (sa) => sa.sensorNumber === sensorItem.n && sa.sensorType === 'TOR'
    )
    sensorItem.name = relatedSensorAssignment?.name || sensorItem.name

    // Check if this is a duration TOR
    const isDurationTOR = sensorAssignments.some(
      (sa) =>
        sa.sensorNumber === sensorItem.n &&
        sa.isDurationTOR &&
        sa.sensorType === 'TOR'
    )

    let activedItemIndex = activeTorPositions.findIndex(
      (activeTorItem) => activeTorItem.n === sensorItem.n
    )
    let activedItem = activeTorPositions[activedItemIndex]

    // If sensor was active and is now disabled, set the endTimestamp
    if (activedItem && sensorItem.enabled === false) {
      console.log(
        `diagnostics-service TOR${sensorItem.n} ${sensorItem.name} turned OFF`,
        {
          timestamp: position.timestamp,
        }
      )
      activeTorPositions[activedItemIndex].endTimestamp = position.timestamp
      activeTorPositions.splice(activedItemIndex, 1)
    }

    // If sensor is enabled and is a duration TOR
    if (sensorItem.enabled === true && isDurationTOR) {
      // If sensor wasn't previously active, add it to active sensors with startTimestamp
      if (!activedItem) {
        console.log(
          `diagnostics-service TOR${sensorItem.n} ${sensorItem.name} turned ON`,
          {
            timestamp: position.timestamp,
          }
        )
        sensorItem.startTimestamp = position.timestamp
        activeTorPositions.push(sensorItem)
      }

      // Special case: If this is the first position and sensor is already enabled,
      // set startTimestamp to the timestamp of this position
      if (isFirstPosition) {
        console.log(
          `diagnostics-service TOR${sensorItem.n} ${sensorItem.name} enabled at first position`,
          {
            timestamp: position.timestamp,
          }
        )
        sensorItem.startTimestamp = position.timestamp

        // Ensure it's in the activeTorPositions array if not already
        if (!activedItem) {
          activeTorPositions.push(sensorItem)
        }
      }

      // Special case: If this is the last position and sensor is still enabled,
      // set endTimestamp to the timestamp of this position
      if (isLastPosition && activedItem) {
        console.log(
          `diagnostics-service TOR${sensorItem.n} ${sensorItem.name} still enabled at last position`,
          {
            timestamp: position.timestamp,
          }
        )
        activedItem.endTimestamp = position.timestamp
      }
    }
  })
}

/**
 * Generates a cache key for storing filter states, based on user and vehicle configuration.
 *
 * @param {string} filter - The name of the filter.
 * @throws Will throw an error if userId, clientId, or boxConfigId is not defined.
 * @returns {string} The generated cache key.
 */
export function mountCacheKeyForVehicleAndFilter(filter) {
  const authenticatedUser = store.state.auth.user_infos
  const userId = authenticatedUser.userId
  const clientId = authenticatedUser.toClientId || authenticatedUser.client_id
  const boxConfigId = store.state.diagnostics.vehicleConfiguration.boxConfigId

  if (userId === '') {
    throw new Error(
      'Unable to mount diagnostic cache key, "userId" is not defined'
    )
  }

  if (clientId === '') {
    throw new Error(
      'Unable to mount diagnostic cache key, "client" is not defined'
    )
  }

  if (boxConfigId === null) {
    throw new Error(
      'Unable to mount diagnostic cache key, "boxConfigurationId" is not defined'
    )
  }

  return `filter/clientId:${clientId}/userId:${userId}/boxConfigId:${boxConfigId}/filter:${filter}`
}

/**
 * Saves the state of a filter locally, if a boxConfigId is present and not equal to -1.
 *
 * @param {string} filter - The name of the filter.
 * @param {boolean} showed - The state of the filter to be saved.
 * @async
 */
export async function saveFilterStateLocally(filter, showed) {
  const boxConfigId = store.state.diagnostics.vehicleConfiguration.boxConfigId

  if (boxConfigId && boxConfigId !== -1) {
    const key = mountCacheKeyForVehicleAndFilter(filter)

    await storage.setItem(key, showed)
  }
}

/**
 * Retrieves the locally stored state of a filter.
 *
 * @param {string} filter - The name of the filter.
 * @param {boolean} defaultValue - The default value to return if the state is not found.
 * @returns {Promise<*>} The stored state, or the default value if not found.
 * @async
 */
export async function getFilterStateLocally(filter, defaultValue) {
  const boxConfigId = store.state.diagnostics.vehicleConfiguration.boxConfigId

  if (!boxConfigId || boxConfigId === -1) {
    return defaultValue ?? true
  }

  const key = mountCacheKeyForVehicleAndFilter(filter)

  return await storage.getItem(key, defaultValue)
}
